import { REQUIREMENT_CATALOG } from '@milepost/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { RequestWorkspace } from './RequestWorkspace';
import './Login.css';

/**
 * The signed-out page — which makes it the product's public face, because there is no separate
 * marketing site: compliance.milepostsafety.com serves this to anyone who is not signed in.
 *
 * Two audiences arrive, with opposite needs:
 *
 *  - A safety manager signing in for the fifth time this week. The form stays in the first
 *    viewport on desktop and comes FIRST on a phone — a returning user must never scroll
 *    through marketing to reach the password field.
 *  - A safety or compliance director deciding whether this was built by someone who knows the
 *    regulation. For them the page scrolls: the actual requirement index generated from the
 *    same catalog the engine evaluates (it cannot drift from what the product does), the June
 *    2025 medical-certification change most software still gets wrong, how the product works,
 *    and a request form wired to POST /api/demo-requests.
 *
 * The conversion path is the form, not a mailto. compliance.manager@milepostsafety.com cannot
 * receive mail yet (see DEPLOY.md), and a published address that silently drops a prospect's
 * email is the same failure mode this product exists to prevent — belief that a message was
 * received when nobody is watching. The form writes to Postgres before any mail is attempted.
 */

/** Grouped for a reader, not by the engine's internal recurrence kinds. */
const GROUPS: Array<{ label: string; codes: string[] }> = [
  {
    label: 'Before they drive',
    codes: [
      'EMPLOYMENT_APPLICATION',
      'PRE_EMPLOYMENT_MVR',
      'ROAD_TEST',
      'SAFETY_PERFORMANCE_HISTORY',
      'CLEARINGHOUSE_PRE_EMPLOYMENT',
      'DA_PRE_EMPLOYMENT_TEST',
    ],
  },
  {
    label: 'Every twelve months',
    codes: ['ANNUAL_MVR', 'ANNUAL_REVIEW', 'CLEARINGHOUSE_ANNUAL'],
  },
  {
    label: 'After each medical exam',
    codes: ['MED_CERT_MVR_CDL', 'MED_CERT_PAPER_NON_CDL', 'NRCME_VERIFICATION'],
  },
];

export function Login() {
  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        label: g.label,
        items: g.codes
          .map((code) => REQUIREMENT_CATALOG.find((r) => r.code === code))
          .filter((r): r is NonNullable<typeof r> => Boolean(r)),
      })),
    [],
  );

  return (
    <div className="login">
      {/* ── Hero: the claim on the left, the door on the right ─────────────── */}
      <section className="login-hero">
        <div className="login-panel">
          <div className="login-panel-inner">
            <header className="login-brand">
              <span className="login-mark">Milepost</span>
              <span className="login-sub">Compliance</span>
            </header>

            <div className="login-lede">
              <p className="login-eyebrow">Driver qualification files · 49 CFR part 391</p>
              <h1 className="login-title">Audit‑ready driver files, before the audit.</h1>
              <p className="login-blurb">
                Every §391.51 requirement, tracked against every driver, with an email to your
                safety team before anything lapses — not after an auditor finds it.
              </p>
            </div>

            <ul className="login-proof">
              <li>
                Every requirement carries its CFR citation on screen, so you can check our work
                rather than take it on faith.
              </li>
              <li>
                Drivers photograph documents from their phones. Nothing counts toward a file
                until your own staff accepts it.
              </li>
              <li>
                Warnings at 60, 30, 14, 7 and 1 day — then daily until it is fixed, evaluated in
                your local calendar date.
              </li>
            </ul>

            <div className="login-ctas">
              <a className="hero-cta" href="#request-workspace">Request a workspace</a>
              <a className="hero-cta-secondary" href="#what-we-track">See what we track</a>
            </div>
          </div>
        </div>

        <div className="login-form-wrap">
          <SignInForm />
        </div>
      </section>

      {/* ── The rulebook, from the engine's own catalog ────────────────────── */}
      <section className="login-section" id="what-we-track">
        <div className="login-section-inner">
          <p className="eyebrow">What we track</p>
          <h2 className="login-h2">The whole §391.51 file, cited line by line</h2>
          <p className="login-section-lede">
            This index is generated from the same catalog the compliance engine evaluates, so it
            cannot drift from what the product actually checks.
          </p>

          <div className="reg-index" aria-label="What a driver qualification file must contain">
            {groups.map((group) => (
              <div className="reg-group" key={group.label}>
                <h3 className="reg-group-label">{group.label}</h3>
                <ul className="reg-list">
                  {group.items.map((r) => (
                    <li className="reg-item" key={r.code}>
                      <span className="reg-title">{r.title}</span>
                      <span className="reg-cite">{r.cfrCite}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <aside className="wedge">
            <h3 className="wedge-title">
              The June 2025 medical‑certification change is where files quietly break
            </h3>
            <p>
              Since <strong>23 June 2025</strong>, medical examiners transmit results to the
              FMCSA National Registry and the state posts certification status to the driver's
              MVR. For CDL and CLP holders, a copy of the paper certificate is{' '}
              <strong>no longer valid proof</strong> — the file needs an MVR showing current
              certification status. Non‑CDL drivers are unchanged.
            </p>
            <p>
              Software that files a med card for everyone leaves CDL files deficient. We model
              the split per driver. <span className="cite">49 CFR 391.51(b)(7)(ii)</span>
            </p>
          </aside>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────────────────────────── */}
      <section className="login-section is-alt">
        <div className="login-section-inner">
          <p className="eyebrow">How it works</p>
          <h2 className="login-h2">Three jobs, done continuously</h2>

          <ol className="steps">
            <li className="step">
              <span className="step-num" aria-hidden="true">1</span>
              <h3 className="step-title">Drivers send documents from their phones</h3>
              <p>
                One tap from a text message — no app store, no username to remember. Photos are
                held on the phone until they reach us, so a dead zone at a truck stop cannot
                lose a document. Nothing is accepted into a file until your staff reviews it.
              </p>
            </li>
            <li className="step">
              <span className="step-num" aria-hidden="true">2</span>
              <h3 className="step-title">Every file is evaluated daily, to the regulation</h3>
              <p>
                Each driver is checked against the full requirement catalog every night, in your
                own timezone's calendar date. Every status on screen names the section of 49 CFR
                it comes from.
              </p>
            </li>
            <li className="step">
              <span className="step-num" aria-hidden="true">3</span>
              <h3 className="step-title">You hear about it before it lapses</h3>
              <p>
                One digest to your safety team — worst first — at 60, 30, 14, 7 and 1 day out,
                then daily once overdue. Drivers are nudged only about what they can supply
                themselves. Bounced alerts surface as warnings instead of vanishing.
              </p>
            </li>
          </ol>
        </div>
      </section>

      {/* ── Conversion ─────────────────────────────────────────────────────── */}
      <section className="login-section" id="request-workspace">
        <div className="login-section-inner is-narrow">
          <p className="eyebrow">Getting started</p>
          <h2 className="login-h2">Request a workspace</h2>
          <p className="login-section-lede">
            There is no self‑serve signup, deliberately: these files hold your drivers'
            regulated records, and every workspace is set up personally by Milepost Safety
            Group. Tell us about your fleet and we will schedule a short walkthrough.
          </p>
          <RequestWorkspace />
        </div>
      </section>

      <footer className="login-page-foot">
        <div className="login-section-inner">
          Milepost Compliance is a service of{' '}
          <a href="https://milepostsafety.com">Milepost Safety Group</a> — DOT and FMCSA safety
          consulting and expert‑witness work.
        </div>
      </footer>
    </div>
  );
}

/** The sign-in card. Split out so the page around it can change without touching auth. */
function SignInForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const signIn = useMutation({
    mutationFn: () => api.post('/api/auth/login', { email, password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate('/', { replace: true });
    },
  });

  const fieldErrors = signIn.error instanceof ApiError ? signIn.error.byField() : {};
  const generalError =
    signIn.error && Object.keys(fieldErrors).length === 0 ? (signIn.error as Error).message : null;

  return (
    <form
      className="login-form"
      onSubmit={(e) => {
        e.preventDefault();
        signIn.mutate();
      }}
    >
      <h2 className="login-form-title">Sign in</h2>
      <p className="login-form-sub">Carrier workspace</p>

      <div className="field">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          // Convenience for the daily visitor — but never when arriving at an anchor, where
          // stealing focus would yank the page back up to the hero.
          autoFocus={!window.location.hash}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={Boolean(fieldErrors.email)}
          required
        />
        {fieldErrors.email && <p className="field-error">{fieldErrors.email}</p>}
      </div>

      <div className="field">
        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>

      {generalError && (
        <p className="login-alert" role="alert">
          {generalError}
        </p>
      )}

      <button type="submit" className="btn login-submit" disabled={signIn.isPending}>
        {signIn.isPending ? 'Signing in…' : 'Sign in'}
      </button>

      <p className="login-help">
        New here? <a href="#request-workspace">Request a workspace</a> — every account is set up
        personally.
      </p>
      <p className="login-help login-help-driver">
        Drivers: use the link your safety department sent you.
      </p>
    </form>
  );
}
